import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

const apiUrlTipos = `${(process.env.REACT_APP_API_PLATOS || 'http://localhost:8085/api/platos/').replace(/\/+$/, '')}/../tipos-plato`;

const EditarTipoPlato = () => {
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [activo, setActivo] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalNombre, setOriginalNombre] = useState('');
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        getTipo();
    }, [id]);

    const getTipo = async () => {
        try {
            const response = await axios.get(`${apiUrlTipos}/${id}`);
            const tipo = response.data;
            setNombre(tipo.nombre);
            setOriginalNombre(tipo.nombre);
            setDescripcion(tipo.descripcion || '');
            setActivo(tipo.activo);
        } catch (error) {
            console.error('Error al obtener tipo de plato:', error);
            alert('❌ Error al cargar el tipo de plato');
            navigate('/admin/tipos-plato');
        } finally {
            setIsLoading(false);
        }
    };

    const update = async (e) => {
        e.preventDefault();

        if (nombre.trim() === '') {
            alert('⚠️ El nombre es requerido');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.put(`${apiUrlTipos}/${id}`, {
                nombre: nombre.trim(),
                descripcion: descripcion.trim() || null,
                activo
            });
            alert('✅ Tipo de plato actualizado correctamente');
            navigate('/admin/tipos-plato');
        } catch (error) {
            console.error('Error al actualizar tipo de plato:', error);
            if (error.response?.data?.message) {
                alert('❌ ' + error.response.data.message);
            } else {
                alert('❌ Error al actualizar el tipo de plato');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/tipos-plato');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                    <div className="flex items-center gap-4">
                        <svg className="animate-spin h-8 w-8 text-rose-600" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-lg font-semibold text-gray-700">Cargando tipo de plato...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-100 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-xl p-6 border-b-4 border-pink-500">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-4 rounded-xl shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Editar Tipo de Plato</h1>
                            <p className="text-gray-600 mt-1">Modifica <span className="font-semibold text-pink-600">{originalNombre}</span></p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={update} className="bg-white rounded-b-2xl shadow-xl p-8">
                    <div className="space-y-6">
                        {/* Nombre */}
                        <div>
                            <label htmlFor="nombre" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-rose-600">🏷️</span>
                                Nombre del Tipo
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="nombre"
                                placeholder="Ej: General, Veggie, Pechuga, Celiaco, etc."
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Descripción */}
                        <div>
                            <label htmlFor="descripcion" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-pink-600">📝</span>
                                Descripción
                                <span className="text-gray-500 text-xs font-normal">(opcional)</span>
                            </label>
                            <textarea
                                id="descripcion"
                                placeholder="Descripción del tipo de plato..."
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 transition-all outline-none text-gray-800 placeholder-gray-400 resize-none"
                            />
                        </div>

                        {/* Activo */}
                        <div>
                            <label className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-100 transition-all">
                                <input
                                    type="checkbox"
                                    checked={activo}
                                    onChange={(e) => setActivo(e.target.checked)}
                                    className="w-5 h-5 text-rose-600 rounded focus:ring-2 focus:ring-rose-500"
                                />
                                <div className="flex-1">
                                    <span className="font-semibold text-gray-700">Tipo activo</span>
                                    <p className="text-sm text-gray-500">Solo los tipos activos aparecerán al crear platos</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-pink-700 hover:to-rose-700 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Guardar Cambios
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </span>
                        </button>
                    </div>
                </form>

                {/* Info adicional */}
                <div className="mt-6 bg-pink-50 border-l-4 border-pink-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-pink-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-pink-900">Ten en cuenta</p>
                            <p className="text-sm text-pink-700 mt-1">
                                Si desactivas un tipo de plato, no aparecerá en los selectores pero los platos existentes de ese tipo se mantendrán sin cambios.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditarTipoPlato;
